import { normalizeUrl } from '@disco/accounts/utils/normalize-url'
import { useMemo } from 'react'
import { useTranslation } from '@disco/i18n/use-translation'

const INSTAGRAM_HANDLE = 'instagram_handle'
const TIKTOK_HANDLE = 'tiktok_handle'
const TWITTER_HANDLE = 'twitter_handle'
const BANDCAMP_URL = 'bandcamp_url'
const APPLE_MUSIC_URL = 'apple_music_url'
const YOUTUBE_URL = 'youtube_url'
const SOUNDCLOUD_URL = 'soundcloud_url'
const FACEBOOK_URL = 'facebook_url'
const WEBSITE_URL = 'website_url'
const ROSTR_URL = 'rostr_url'
const SPOTIFY_URL = 'spotify_url'
const BUSINESS_URL = 'business_url'
const MARKETING_OPTIN = 'marketing_optin'

const socialMediaHandlesFieldNames = [
  INSTAGRAM_HANDLE,
  TIKTOK_HANDLE,
  TWITTER_HANDLE,
] as const

const urlFieldNames = [
  BANDCAMP_URL,
  APPLE_MUSIC_URL,
  YOUTUBE_URL,
  SOUNDCLOUD_URL,
  FACEBOOK_URL,
  WEBSITE_URL,
  ROSTR_URL,
  SPOTIFY_URL,
  BUSINESS_URL,
] as const

export const handleSourceUrlBlur = (
  name: string,
  value: string | undefined,
) => {
  const shouldNormalizeHandle = socialMediaHandlesFieldNames.includes(
    name as (typeof socialMediaHandlesFieldNames)[number],
  )

  if (value && shouldNormalizeHandle) {
    const normalizedValue = normalizeHandles(
      value,
      name as (typeof socialMediaHandlesFieldNames)[number],
    )

    return normalizedValue
  }

  const shouldNormalizeUrl = urlFieldNames.includes(
    name as (typeof urlFieldNames)[number],
  )

  if (shouldNormalizeUrl) {
    const normalizedValue = normalizeUrl(value)
    // TODO: set input value to normalizedValue
    return normalizedValue
  }
  return value
}

const HANDLE_URLS = {
  [INSTAGRAM_HANDLE]: 'https://www.instagram.com/',
  [TIKTOK_HANDLE]: 'https://www.tiktok.com/',
  [TWITTER_HANDLE]: 'https://twitter.com/',
}

function normalizeHandles(url: string, type: keyof typeof HANDLE_URLS) {
  const handleUrl = HANDLE_URLS[type]
  if (handleUrl) {
    return url.replace(handleUrl, '').replace(/^@/, '').replace(/\//, '')
  } else {
    return url
  }
}

// Static data model for signup flow - matching disco-apps structure
export interface SignupInput {
  type:
    | 'text'
    | 'select'
    | 'radio'
    | 'file'
    | 'checkbox'
    | 'verification_pair'
    | 'type_selection'
  name: string
  label: string
  placeholder?: string
  required?: boolean
  options?: Array<{
    label: string
    value: string
    description?: string
    icon?: string
  }>
  accept?: string
  fields?: SignupInput[]
}

export interface SignupStep {
  title: string
  inputs: SignupInput[]
  submit?: {
    text: string
    processingText?: string
  }
}

export interface SignupType {
  user: {
    key: string
    title: string
    description: string
  }
  steps: SignupStep[]
}

// Mapping of business types to role options (mirrors disco-apps behavior)
export function useBusinessRoleOptions() {
  const { t } = useTranslation('accounts')
  return useMemo(() => {
    const BUSINESS_ROLE_OPTIONS: Record<
      string,
      Array<{ label: string; value: string }>
    > = {
      music_supervision: [
        {
          label: t(
            'accounts:musicSupervisionCompanyInhouse',
            'Music Supervision Company (in-house)',
          ),
          value: 'music_supervision_company_in_house',
        },
        {
          label: t('accounts:freelanceSupervisor', 'Freelance Supervisor'),
          value: 'freelance_supervisor',
        },
        {
          label: t('accounts:trailerInhouse', 'Trailer (in-house)'),
          value: 'trailer_in_house',
        },
        {
          label: t('accounts:adAgencyInhouse', 'Ad Agency (in-house)'),
          value: 'ad_agency_in_house',
        },
        {
          label: t('accounts:studioInhouse', 'Studio (in-house)'),
          value: 'studio_in_house',
        },
        {
          label: t('accounts:networkInhouse', 'Network (in-house)'),
          value: 'network_in_house',
        },
        {
          label: t('accounts:playlisting', 'Playlisting'),
          value: 'playlisting',
        },
      ],
      post_production: [
        {
          label: t('accounts:musicEditor', 'Music Editor'),
          value: 'music_editor',
        },
        {
          label: t('accounts:videoEditor', 'Video Editor'),
          value: 'video_editor',
        },
        { label: t('accounts:producer', 'Producer'), value: 'producer' },
      ],
      label: [
        {
          label: t('accounts:syncCreative', 'Sync Creative'),
          value: 'sync_creative',
        },
        { label: t('accounts:ar', 'A&R'), value: 'a_r' },
        {
          label: t('accounts:marketingPr', 'Marketing/PR'),
          value: 'marketing_pr',
        },
        {
          label: t(
            'accounts:commercialDspPlaylisting',
            'Commercial DSP Playlisting',
          ),
          value: 'commercial_dsp_playlisting',
        },
        {
          label: t('accounts:catalogManager', 'Catalog Manager'),
          value: 'catalogue_manager',
        },
      ],
      publisher: [
        {
          label: t('accounts:syncCreative', 'Sync Creative'),
          value: 'sync_creative',
        },
        { label: t('accounts:ar', 'A&R'), value: 'a_r' },
        {
          label: t('accounts:catalogManager', 'Catalog Manager'),
          value: 'catalogue_manager',
        },
      ],
      sync_agency_rep: [
        {
          label: t('accounts:syncCreative', 'Sync Creative'),
          value: 'sync_creative',
        },
        {
          label: t('accounts:catalogManager', 'Catalog Manager'),
          value: 'catalogue_manager',
        },
        {
          label: t('accounts:coordinator', 'Coordinator'),
          value: 'coordinator',
        },
      ],
      production_music_library: [
        {
          label: t(
            'accounts:musicSupervisorResearcher',
            'Music Supervisor Researcher',
          ),
          value: 'music_supervisor_researcher',
        },
        {
          label: t('accounts:catalogManager', 'Catalog Manager'),
          value: 'catalogue_manager',
        },
        { label: t('accounts:ar', 'A&R'), value: 'a_r' },
      ],
      artist_management: [
        { label: t('accounts:manager', 'Manager'), value: 'manager' },
        {
          label: t('accounts:talentAgency', 'Talent Agency'),
          value: 'talent_agency',
        },
      ],
      promotions: [
        { label: t('accounts:radio', 'Radio'), value: 'radio' },
        { label: t('accounts:pr', 'PR'), value: 'pr' },
        {
          label: t('accounts:digitalMarketing', 'Digital Marketing'),
          value: 'digital_marketing',
        },
        {
          label: t('accounts:dspPlaylisting', 'DSP Playlisting'),
          value: 'dsp_playlisting',
        },
      ],
      radio_station: [
        {
          label: t('accounts:programManager', 'Program Manager'),
          value: 'program_manager',
        },
        {
          label: t('accounts:musicDirector', 'Music Director'),
          value: 'music_director',
        },
        { label: t('accounts:dj', 'DJ'), value: 'dj' },
      ],
      podcast_content_creation: [
        { label: t('accounts:podcaster', 'Podcaster'), value: 'podcaster' },
        {
          label: t('accounts:contentCreator', 'Content Creator'),
          value: 'content_creator',
        },
      ],
    }
    return BUSINESS_ROLE_OPTIONS
  }, [t])
}

export function useSignupTypes() {
  const { t } = useTranslation('accounts')
  return useMemo(() => {
    // Base steps (email + name/type selection)
    const BASE_STEPS: SignupStep[] = [
      {
        title: t('accounts:getStartedWithDisco', 'Get started with DISCO'),
        inputs: [
          {
            type: 'text',
            name: 'email',
            label: t('accounts:emailAddress', 'Email'),
            placeholder: t(
              'accounts:enterYourEmailAddress',
              'example@email.com',
            ),
            required: true,
          },
        ],
        submit: {
          text: t('accounts:startAFreeTrial', 'Start a free trial'),
        },
      },
      {
        title: t('accounts:tellUsABitAboutYou', 'Tell us a bit about you'),
        inputs: [
          {
            type: 'text',
            // i18next-instrument-ignore
            name: 'fullName',
            label: t('accounts:fullName', 'Full Name'),
            placeholder: t('accounts:johnSmith', 'John Smith'),
            required: true,
          },
          {
            type: 'type_selection',
            // i18next-instrument-ignore
            name: 'userType',
            label: t(
              'accounts:whatBestDescribesYourWork',
              'What best describes your work?',
            ),
            required: true,
            options: [
              {
                label: t('accounts:artist', 'Artist'),
                value: 'artist',
                description: t(
                  'accounts:discoForArtistsSongwritersProducersDjsComposersEngineersBandsAndOtherMusicCreators',
                  'DISCO for artists, songwriters, producers, DJs, composers, engineers, bands and other music creators.',
                ),
                icon: 'artist',
              },
              {
                label: t('accounts:business', 'Business'),
                value: 'business',
                description: t(
                  'accounts:discoForRecordLabelsPublishersSupervisorsSyncRepsAndOtherBusinesses',
                  'DISCO for record labels, publishers, supervisors, sync reps and other businesses.',
                ),
                icon: 'business',
              },
            ],
          },
        ],
        submit: {
          text: t('accounts:continue', 'Continue'),
        },
      },
    ]

    // Artist type steps
    const ARTIST_STEPS: SignupStep[] = [
      {
        title: t('accounts:tellUsAboutYourWork', 'Tell us about your work:'),
        inputs: [
          {
            type: 'text',
            // i18next-instrument-ignore
            name: 'business_name',
            label: t('accounts:artistbandName', 'Artist/Band Name:'),
            placeholder: t(
              'accounts:artistbandNamePlaceholder',
              'Artist/Band name',
            ),
            required: true,
          },
          {
            type: 'text',
            // i18next-instrument-ignore
            name: 'subdomain',
            label: t('accounts:discoUrl', 'DISCO URL:'),
            placeholder: 'artistname',
            required: true,
          },
        ],
        submit: {
          text: t('accounts:next', 'Next'),
        },
      },
      {
        title: t('accounts:tellUsAboutYourWork', 'Tell us about your work:'),
        inputs: [
          {
            type: 'select',
            // i18next-instrument-ignore
            name: 'user_role',
            label: t(
              'accounts:whatBestDescribesYourRole',
              'What best describes your role?',
            ),
            placeholder: t('accounts:pleaseSelect', 'Please select'),
            required: true,
            options: [
              { label: t('accounts:artist', 'Artist'), value: 'artist' },
              { label: t('accounts:band', 'Band'), value: 'band' },
              { label: t('accounts:composer', 'Composer'), value: 'composer' },
              { label: t('accounts:producer', 'Producer'), value: 'producer' },
              { label: t('accounts:engineer', 'Engineer'), value: 'engineer' },
              {
                label: t('accounts:songwriter', 'Songwriter'),
                value: 'songwriter',
              },
            ],
          },
        ],
        submit: {
          text: t('accounts:next', 'Next'),
        },
      },
      {
        title: t(
          'accounts:almostThereFinalStep',
          'Almost there! Final step...',
        ),
        inputs: [
          {
            type: 'verification_pair',
            // i18next-instrument-ignore
            name: 'verification_1',
            label: t(
              'accounts:pleaseProvideTwoSourcesOfVerification',
              'Please provide two sources of verification:',
            ),
            required: true,
            fields: [
              {
                type: 'select',
                // i18next-instrument-ignore
                name: 'verification_source_1',
                label: '',
                placeholder: t(
                  'accounts:enterYourInstagramUsername',
                  'Enter your Instagram username',
                ),
                required: true,
                options: [
                  {
                    label: t('accounts:instagram', 'Instagram'),
                    value: 'instagram_handle',
                  },
                  {
                    label: t('accounts:spotify', 'Spotify'),
                    value: 'spotify_url',
                  },
                  {
                    label: t('accounts:tiktokUsername', 'TikTok username'),
                    value: 'tiktok_handle',
                  },
                  {
                    label: t('accounts:websiteUrl', 'Website URL'),
                    value: 'website_url',
                  },
                  {
                    label: t('accounts:ipiNumber', 'IPI Number'),
                    value: 'ipi_number',
                  },
                  {
                    label: t('accounts:facebookUrl', 'Facebook URL'),
                    value: 'facebook_url',
                  },
                  {
                    label: t('accounts:soundcloudUrl', 'SoundCloud URL'),
                    value: 'soundcloud_url',
                  },
                  {
                    label: t('accounts:appleMusic', 'Apple Music'),
                    value: 'apple_music_url',
                  },
                  {
                    label: t('accounts:bandcamp', 'Bandcamp'),
                    value: 'bandcamp_url',
                  },
                  {
                    label: t('accounts:tidal', 'Tidal'),
                    value: 'tidal_music_url',
                  },
                  {
                    label: t('accounts:twitter', 'Twitter'),
                    value: 'twitter_handle',
                  },
                  {
                    label: t('accounts:youtube', 'YouTube'),
                    value: 'youtube_url',
                  },
                  {
                    label: t('accounts:other', 'Other'),
                    value: 'other_description',
                  },
                ],
              },
              {
                type: 'text',
                // i18next-instrument-ignore
                name: 'verification_handle_1',
                label: '',
                placeholder: t(
                  'accounts:enterYourInstagramUsername',
                  'Enter your Instagram username',
                ),
                required: true,
              },
            ],
          },
          {
            type: 'verification_pair',
            // i18next-instrument-ignore
            name: 'verification_2',
            label: '',
            required: true,
            fields: [
              {
                type: 'select',
                // i18next-instrument-ignore
                name: 'verification_source_2',
                label: '',
                placeholder: t(
                  'accounts:enterYourSpotifyUrl',
                  'Enter your Spotify URL',
                ),
                required: true,
                options: [
                  {
                    label: t('accounts:instagram', 'Instagram'),
                    value: 'instagram_handle',
                  },
                  {
                    label: t('accounts:spotify', 'Spotify'),
                    value: 'spotify_url',
                  },
                  {
                    label: t('accounts:tiktokUsername', 'TikTok username'),
                    value: 'tiktok_handle',
                  },
                  {
                    label: t('accounts:websiteUrl', 'Website URL'),
                    value: 'website_url',
                  },
                  {
                    label: t('accounts:ipiNumber', 'IPI Number'),
                    value: 'ipi_number',
                  },
                  {
                    label: t('accounts:facebookUrl', 'Facebook URL'),
                    value: 'facebook_url',
                  },
                  {
                    label: t('accounts:soundcloudUrl', 'SoundCloud URL'),
                    value: 'soundcloud_url',
                  },
                  {
                    label: t('accounts:appleMusic', 'Apple Music'),
                    value: 'apple_music_url',
                  },
                  {
                    label: t('accounts:bandcamp', 'Bandcamp'),
                    value: 'bandcamp_url',
                  },
                  {
                    label: t('accounts:tidal', 'Tidal'),
                    value: 'tidal_music_url',
                  },
                  {
                    label: t('accounts:twitter', 'Twitter'),
                    value: 'twitter_handle',
                  },
                  {
                    label: t('accounts:youtube', 'YouTube'),
                    value: 'youtube_url',
                  },
                  {
                    label: t('accounts:other', 'Other'),
                    value: 'other_description',
                  },
                ],
              },
              {
                type: 'text',
                // i18next-instrument-ignore
                name: 'verification_handle_2',
                label: '',
                placeholder: t(
                  'accounts:enterYourSpotifyUrl',
                  'Enter your Spotify URL',
                ),
                required: true,
              },
            ],
          },
          {
            type: 'checkbox',
            // i18next-instrument-ignore
            name: 'termsAcceptance',
            label: t(
              'accounts:iAgreeToTheTermsOfServiceAndPrivacyPolicy',
              'I agree to the Terms of Service and Privacy Policy',
            ),
            required: true,
          },
          {
            type: 'checkbox',
            // i18next-instrument-ignore
            name: MARKETING_OPTIN,
            label: t(
              'accounts:itsOkToEmailMeAboutTheDiscoService',
              'It’s ok to email me about the DISCO service.',
            ),
            required: false,
          },
        ],
        submit: {
          text: t('accounts:createAccount', 'Create account'),
          processingText: t('accounts:creatingAccount', 'Creating account...'),
        },
      },
    ]

    // Business type steps
    const BUSINESS_STEPS: SignupStep[] = [
      {
        title: t('accounts:tellUsAboutYourWork', 'Tell us about your work:'),
        inputs: [
          {
            type: 'text',
            // i18next-instrument-ignore
            name: 'business_name',
            label: t('accounts:businessName', 'Business Name:'),
            placeholder: t('accounts:businessNamePlaceholder', 'Business name'),
            required: true,
          },
          {
            type: 'text',
            // i18next-instrument-ignore
            name: 'subdomain',
            label: t('accounts:discoUrl', 'DISCO URL:'),
            placeholder: 'businessname',
            required: true,
          },
          {
            type: 'file',
            // i18next-instrument-ignore
            name: 'logo',
            label: t('accounts:uploadCompanyLogo', 'Upload Company Logo'),
            placeholder: t('accounts:uploadFile', 'Upload file'),
            accept: 'image/*',
            required: false,
          },
        ],
        submit: {
          text: t('accounts:next', 'Next'),
        },
      },
      {
        title: t('accounts:tellUsAboutYourWork', 'Tell us about your work:'),
        inputs: [
          {
            type: 'select',
            // i18next-instrument-ignore
            name: 'businessType',
            label: t(
              'accounts:whatBestDescribesYourBusiness',
              'What best describes your business?',
            ),
            placeholder: t('accounts:pleaseSelect', 'Please select'),
            required: true,
            options: [
              {
                label: t('accounts:musicSupervision', 'Music Supervision'),
                value: 'music_supervision',
              },
              {
                label: t('accounts:postproduction', 'Post-production'),
                value: 'post_production',
              },
              { label: t('accounts:label', 'Label'), value: 'label' },
              {
                label: t('accounts:publisher', 'Publisher'),
                value: 'publisher',
              },
              {
                label: t('accounts:syncAgencyRep', 'Sync Agency / Rep'),
                value: 'sync_agency_rep',
              },
              {
                label: t(
                  'accounts:productionMusicLibrary',
                  'Production Music Library',
                ),
                value: 'production_music_library',
              },
              {
                label: t('accounts:artistManagement', 'Artist Management'),
                value: 'artist_management',
              },
              {
                label: t('accounts:promotions', 'Promotions'),
                value: 'promotions',
              },
              {
                label: t('accounts:radioStation', 'Radio Station'),
                value: 'radio_station',
              },
              {
                label: t(
                  'accounts:podcastContentCreation',
                  'Podcast / Content Creation',
                ),
                value: 'podcast_content_creation',
              },
            ],
          },
          {
            type: 'select',
            // i18next-instrument-ignore
            name: 'user_role',
            label: t(
              'accounts:whatBestDescribesYourRole',
              'What best describes your role?',
            ),
            placeholder: t('accounts:pleaseSelect', 'Please select'),
            required: true,
            options: [],
          },
          {
            type: 'select',
            // i18next-instrument-ignore
            name: 'teamSize',
            label: t('accounts:sizeOfYourTeam', 'Size of your team'),
            placeholder: t('accounts:pleaseSelect', 'Please select'),
            required: true,
            options: [
              { label: '1', value: '1' },
              { label: '2-5', value: '2-5' },
              { label: '6-10', value: '6-10' },
              { label: t('accounts:moreThan10', 'More than 10'), value: '10+' },
            ],
          },
          {
            type: 'select',
            // i18next-instrument-ignore
            name: 'catalogSize',
            label: t('accounts:catalogSize', 'Catalog size'),
            placeholder: t('accounts:pleaseSelect', 'Please select'),
            required: true,
            options: [
              {
                label: t('accounts:lessThan1000', 'Less than 1000'),
                value: '<1000',
              },
              { label: '1000-5000', value: '1000-5000' },
              { label: '5001-10000', value: '5001-10000' },
              { label: '10001-20000', value: '10001-20000' },
              {
                label: t('accounts:moreThan20000', 'More than 20000'),
                value: '20000+',
              },
            ],
          },
        ],
        submit: {
          text: t('accounts:next', 'Next'),
        },
      },
      {
        title: t('accounts:verifyYourAccount', 'Verify your account'),
        inputs: [
          {
            type: 'verification_pair',
            // i18next-instrument-ignore
            name: 'verification_1',
            label: t(
              'accounts:pleaseProvideOneSourceOfVerification',
              'Please provide one source of verification:',
            ),
            required: true,
            fields: [
              {
                type: 'select',
                // i18next-instrument-ignore
                name: 'verification_source',
                label: '',
                placeholder: t('accounts:instagramUser', 'Instagram user...'),
                required: true,
                options: [
                  {
                    label: t('accounts:websiteUrl', 'Website URL'),
                    value: 'website_url',
                  },
                  {
                    label: t(
                      'accounts:instagramUsername',
                      'Instagram username',
                    ),
                    value: 'instagram_handle',
                  },
                  {
                    label: t('accounts:tiktokUsername', 'TikTok username'),
                    value: 'tiktok_handle',
                  },
                  {
                    label: t('accounts:facebookUrl', 'Facebook URL'),
                    value: 'facebook_url',
                  },
                  {
                    label: t('accounts:appleMusic', 'Apple Music'),
                    value: 'apple_music_url',
                  },
                  {
                    label: t('accounts:bandcamp', 'Bandcamp'),
                    value: 'bandcamp_url',
                  },
                  {
                    label: t('accounts:ipiNumber', 'IPI Number'),
                    value: 'ipi_number',
                  },
                  {
                    label: t('accounts:soundcloudUrl', 'SoundCloud URL'),
                    value: 'soundcloud_url',
                  },
                  {
                    label: t('accounts:spotify', 'Spotify'),
                    value: 'spotify_url',
                  },
                  {
                    label: t('accounts:tidal', 'Tidal'),
                    value: 'tidal_music_url',
                  },
                  {
                    label: t('accounts:twitter', 'Twitter'),
                    value: 'twitter_handle',
                  },
                  {
                    label: t('accounts:youtube', 'YouTube'),
                    value: 'youtube_url',
                  },
                  {
                    label: t('accounts:other', 'Other'),
                    value: 'other_description',
                  },
                ],
              },
              {
                type: 'text',
                // i18next-instrument-ignore
                name: 'verification_handle',
                label: '',
                placeholder: t(
                  'accounts:enterYourWebsiteUrl',
                  'Enter your Website URL',
                ),
                required: true,
              },
            ],
          },
          {
            type: 'checkbox',
            // i18next-instrument-ignore
            name: 'termsAcceptance',
            label: t(
              'accounts:iAgreeToTheTermsOfServiceAndPrivacyPolicy',
              'I agree to the Terms of Service and Privacy Policy',
            ),
            required: true,
          },
          {
            type: 'checkbox',
            // i18next-instrument-ignore
            name: MARKETING_OPTIN,
            label: t(
              'accounts:itsOkToEmailMeAboutTheDiscoService',
              'It’s ok to email me about the DISCO service.',
            ),
            required: false,
          },
        ],
        submit: {
          text: t('accounts:createAccount', 'Create account'),
          processingText: t('accounts:creatingAccount', 'Creating account...'),
        },
      },
    ]

    // Complete signup types configuration
    const SIGNUP_TYPES: SignupType[] = [
      {
        user: {
          key: 'email',
          title: t('accounts:email', 'Email'),
          description: t(
            'accounts:startWithYourEmail',
            'Start with your email',
          ),
        },
        steps: BASE_STEPS,
      },
      {
        user: {
          key: 'name',
          title: 'Name',
          description: t('accounts:tellUsAboutYou', 'Tell us about you'),
        },
        steps: BASE_STEPS,
      },
      {
        user: {
          key: 'artist',
          title: t('accounts:artist', 'Artist'),
          description: t('accounts:iCreateMusic', 'I create music'),
        },
        steps: ARTIST_STEPS,
      },
      {
        user: {
          key: 'business',
          title: t('accounts:business', 'Business'),
          description: t(
            'accounts:iWorkInTheMusicIndustry',
            'I work in the music industry',
          ),
        },
        steps: BUSINESS_STEPS,
      },
    ]
    return SIGNUP_TYPES
  }, [t])
}
